import { default as chakraTheme } from '@chakra-ui/theme'
import { mapKeys } from 'lodash'

type MappedKeysWithPrefix<Prefix extends string, Type> = {
  [Property in keyof Type as `${Prefix}${string & Property}`]: Type[Property]
}

const prefixKeys = <Prefix extends string, T extends Record<string, string>>(
  obj: T,
  prefix: Prefix
): MappedKeysWithPrefix<Prefix, T> =>
  mapKeys(obj, (value, key) => `${prefix}${key}`) as MappedKeysWithPrefix<
    Prefix,
    T
  >

export const colors = {
  brand: '#f22d45',
  white: '#ffffff',
  success: '#51722c',
  successLight: '#deedd3',
  warning: '#985a00',
  warningLight: '#fae6b3',
  error: '#c52d2f',
  errorLight: '#fee1e1',
  seasonal: '#7d479b',
  seasonalLight: '#a96bcc',
  seasonalContrast: '#ffffff',
  licorice: '#161719', // black
  quartz: '#58585f', // greyDark
  manatee: '#a2a2a9',
  ghost: '#d9dadd', // grey
  smoke: '#f5f6f7', // greyLight
  blueCharcoal: '#1f2038',
  vibrantTeal: '#0dd9cb',
}

export const sizeScale = {
  // NOTE: For use in `calc()`, it's important that size '0' has a unit
  '0': '0px',
  '1': '4px',
  '2': '8px',
  '3': '12px',
  '4': '16px',
  '5': '24px',
  '6': '32px',
  '7': '40px',
  '8': '48px',
  '9': '56px',
  '10': '64px',
  '11': '80px',
  '12': '96px',
  '13': '112px',
  '14': '128px',
  '15': '144px',
  '16': '160px',
  '17': '192px',
  '18': '224px',
  '19': '256px',
  '20': '288px',
  '21': '320px',
  '22': '352px',
} as const

export const lineHeights = {
  none: '1em',
  shorter: '1.25em',
  base: '1.5em',
}

export const iconSizes = {
  'size-icon-sm': sizeScale[4],
  'size-icon-md': sizeScale[5],
  'size-icon-lg': sizeScale[8],
  'size-icon-xl': sizeScale[10],
  ...prefixKeys(lineHeights, 'size-icon-line-height-'),
}

export const typographyScale = {
  xs: '12px',
  sm: '14px',
  md: '16px',
  lg: '18px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '28px',
  '4xl': '32px',
  '5xl': '40px',
  '6xl': '48px',
  '7xl': '52px',
} as const

const {
  Button: _Button,
  Tabs: _Tabs,
  Avatar: _Avatar,
  ...componentThemes
} = chakraTheme.components

export const theme = {
  ...chakraTheme,
  space: {
    ...prefixKeys(sizeScale, 'spacing-'),
    // named spacing
    measure: '60ch',
    'spacing-none': sizeScale[0],
    'spacing-xs': sizeScale[1],
    'spacing-sm': sizeScale[2],
    'spacing-md': sizeScale[4],
    'spacing-lg': sizeScale[5],
    'spacing-xl': sizeScale[6],
    'spacing-2xl': sizeScale[7],
    'spacing-3xl': sizeScale[8],
    'spacing-4xl': sizeScale[9],
    'spacing-5xl': sizeScale[10],
  },
  sizes: {
    ...prefixKeys(sizeScale, 'size-'),
    // named sizes
    'size-none': sizeScale[0],
    'size-xs': sizeScale[6],
    'size-sm': sizeScale[10],
    'size-md': sizeScale[14],
    'size-lg': sizeScale[16],
    'size-xl': sizeScale[18],
    'size-2xl': sizeScale[20],
    'size-3xl': sizeScale[22],

    // utility sizes
    'size-full': '100%',
    'size-half': '50%',
    'size-third': '33.333%',
    'size-quarter': '25%',
    'size-fifth': '20%',
    'size-max-fixed': '1152px',
    'size-max-small-fixed': '896px',
    'size-auto': 'auto',
    ...iconSizes,
  },

  fonts: {
    ...chakraTheme.fonts,
    body:
      "'Cera PRO', '-apple-system', 'Roboto', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
    heading:
      "'Cera PRO', '-apple-system', 'Roboto', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
    // A more universal cross-platform monospaced font
    mono:
      "'SFMono-Medium', 'SF Mono', 'Segoe UI Mono', 'Roboto Mono', 'Ubuntu Mono', Menlo, Consolas, Courier, monospace",
  },

  fontSizes: {
    ...prefixKeys(typographyScale, 'font-'),
    'font-promo': typographyScale['7xl'],
    'font-large': typographyScale.lg,
    'font-body': typographyScale.md,
    'font-small': typographyScale.sm,
    'font-tiny': typographyScale.xs,
    'font-caption': typographyScale.xs,

    'font-h1': typographyScale['4xl'],
    'font-h1-mobile': typographyScale['3xl'],
    'font-h2': typographyScale['3xl'],
    'font-h2-mobile': typographyScale['2xl'],
    'font-h3': typographyScale['2xl'],
    'font-h3-mobile': typographyScale.xl,
    'font-h4': typographyScale.xl,
    'font-h4-mobile': typographyScale.lg,
    'font-h5': typographyScale.lg,
    'font-h5-mobile': typographyScale.md,
    'font-h6': typographyScale.lg,
    'font-h6-mobile': typographyScale.md,
  },

  zIndices: {
    hide: -1,
    auto: 'auto',
    base: 0,
    elevatedContent: 1,
    docked: 2,
    dropdown: 3,
    sticky: 4,
    banner: 4,
    overlay: 5,
    modal: 6,
    popover: 7,
    skipLink: 8,
    toast: 9,
    tooltip: 10,
  },

  lineHeights,

  colors,

  iconSizes,

  components: {
    ...componentThemes,
  },
} as const
